import type { TransactionScheduleCallbacks } from "@hooks/view/useViewTableMeta";
import React, { useMemo } from "react";
import { format, getMinutes, getSeconds } from "date-fns";
import { Transaction, TransactionStatus } from "@common/types/transaction";
import {
    fullDateFormatWithTime,
    fullDateFullYearFormat,
} from "@common/helpers/date";
import { formatDateOnly, toBusinessDate } from "@helpers/date";
import { getTransactionAccountNameWithLast4 } from "@common/helpers/transactions";
import { formatForeignCurrency } from "@common/helpers/currency";
import { getLeadingAccountConnection } from "@common/helpers/financialAccount";
import { getSubaccountDisplayName } from "@design-system/Subaccount/utils";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { VStack } from "@design-system/VStack/VStack";
import { TransactionDetailsField } from "./TransactionDetailsField";
import { TransactionInstitution } from "./TransactionInstitution";
import { TransactionDetailsCounterparty } from "./TransactionDetailsCounterparty";
import { TransactionDetailsCustomer } from "./TransactionDetailsCustomer";
import { TransactionDetailsAccount } from "./TransactionDetailsAccount";
import { TransactionDetailsClass } from "./TransactionDetailsClass";
import { TransactionDetailsSchedule } from "./TransactionDetailsSchedule";

interface Props {
    transaction: Transaction;
    isLocked?: boolean;
    scheduleCallbacks?: TransactionScheduleCallbacks;
    /**
     * True when the transaction is matched to an invoice or bill. Owned
     * fields (MATCHED_DOCUMENT_TRANSACTION_OWNERSHIP) are locked.
     */
    isMatchLocked?: boolean;
    /**
     * Tooltip for match-locked owned fields, resolved by the shared
     * getMatchedDocumentFieldLock helper in the parent. Undefined when the
     * field is not match-locked (or is already locked for another reason).
     */
    matchLockTooltip?: string;
}

export const TransactionDetailsMiscFields: React.FC<Props> = ({
    transaction,
    isLocked,
    isMatchLocked,
    matchLockTooltip,
    scheduleCallbacks,
}) => {
    const formattedDate = useMemo(() => {
        if (transaction.dateOnly) {
            return formatDateOnly(transaction.dateOnly, fullDateFullYearFormat);
        }
        const businessDate = toBusinessDate(transaction.date);
        const formatter =
            getMinutes(businessDate) || getSeconds(businessDate)
                ? fullDateFormatWithTime
                : fullDateFullYearFormat;

        return format(businessDate, formatter);
    }, [transaction.date, transaction.dateOnly]);
    const clearedDate = transaction.clearedDateOnly
        ? formatDateOnly(transaction.clearedDateOnly, fullDateFullYearFormat)
        : null;
    const connection = getLeadingAccountConnection(
        transaction.financialAccount,
    );
    const { enabled: isGlFirst } = useIsGlFirst();

    return (
        <VStack gap={16}>
            {connection ? (
                <TransactionDetailsField
                    label="Institution"
                    value={<TransactionInstitution transaction={transaction} />}
                />
            ) : null}

            <TransactionDetailsCounterparty
                isLocked={isLocked || isMatchLocked}
                lockTooltip={matchLockTooltip}
                transaction={transaction}
            />

            <TransactionDetailsCustomer
                isLocked={isLocked}
                transaction={transaction}
            />

            <TransactionDetailsClass
                isLocked={isLocked || isMatchLocked}
                lockTooltip={matchLockTooltip}
                transaction={transaction}
            />

            <TransactionDetailsField
                label="Bank description"
                value={transaction.bankDescription}
            />

            {transaction.foreignCurrency &&
            transaction.foreignCurrencyAmount != null ? (
                <TransactionDetailsField
                    label="Original currency"
                    value={formatForeignCurrency(
                        transaction.foreignCurrencyAmount,
                        transaction.foreignCurrency,
                    )}
                />
            ) : null}

            <TransactionDetailsField label="Date" value={formattedDate} />

            {clearedDate ? (
                <TransactionDetailsField
                    label="Cleared by bank"
                    value={clearedDate}
                />
            ) : null}

            <TransactionDetailsField
                label="Account"
                value={getTransactionAccountNameWithLast4(transaction)}
            />

            {transaction.subaccount ? (
                <TransactionDetailsField
                    label="Subaccount"
                    value={getSubaccountDisplayName(transaction.subaccount)}
                />
            ) : null}

            {transaction.status !== TransactionStatus.PENDING ? (
                <>
                    {/* In GL-first mode the cash GL select is promoted to the
                        category slot in TransactionDetails, so it is omitted
                        here to avoid rendering it twice. */}
                    {!isGlFirst && (
                        <TransactionDetailsAccount
                            isLocked={isLocked || isMatchLocked}
                            lockTooltip={matchLockTooltip}
                            transaction={transaction}
                            basis={AccountingLedgerBasis.cash}
                        />
                    )}
                    <TransactionDetailsAccount
                        isLocked={isLocked || isMatchLocked}
                        lockTooltip={matchLockTooltip}
                        transaction={transaction}
                        basis={AccountingLedgerBasis.accruals}
                    />
                    <TransactionDetailsSchedule
                        isLocked={isLocked}
                        transaction={transaction}
                        scheduleCallbacks={scheduleCallbacks}
                    />
                </>
            ) : null}
        </VStack>
    );
};
